import type * as API from '@sosocio/creator-types/api';
import type * as DB from '@sosocio/creator-types/database';
import reportError from 'utils/report-error';

/**
 * Merges an offerings-data response into the normalized store collection.
 * Incoming models are normalized before the merge mutates existing objects, so
 * references retained by in-flight product setup cannot receive serialized API fields.
 *
 * @param currentOfferings Normalized offering models already held by the store.
 * @param incomingOfferings Offering models received from the offerings-data endpoint.
 * @returns Merged offering models with normalized incoming fields.
 */
export function mergeOfferingModels(
	currentOfferings: DB.OfferingModel[],
	incomingOfferings: API.OfferingDataOfferingModel[],
): DB.OfferingModel[] {
	const mergedOfferings = [...currentOfferings];

	for (const incomingOffering of incomingOfferings) {
		const currentOffering = mergedOfferings.find(({ id }) => id === incomingOffering.id);
		const normalizedOffering = normalizeOfferingModel({
			...currentOffering,
			...incomingOffering,
		});

		if (currentOffering) {
			Object.assign(
				currentOffering,
				normalizedOffering,
			);
		} else {
			mergedOfferings.push(normalizedOffering);
		}
	}

	return mergedOfferings;
}

/**
 * Normalizes offering fields that are expected to be safe at runtime.
 *
 * @param offering Offering model received from an API or already held by the store.
 * @returns Normalized {@link DB.OfferingModel} with safe DPI and spine values.
 */
export function normalizeOfferingModel(
	offering: API.OfferingDataOfferingModel,
): DB.OfferingModel {
	const configDpi = Number(offering.configdpi);
	const qualityDpi = Number(offering.qualitydpi);
	const minimumDpi = Number(offering.minimumdpi);
	let resolvedConfigDpi = configDpi;
	let normalizedSpine: DB.SpineModel[] | undefined;

	if (
		!Number.isFinite(resolvedConfigDpi)
		|| resolvedConfigDpi <= 0
	) {
		if (
			Number.isFinite(qualityDpi)
			&& qualityDpi > 0
		) {
			resolvedConfigDpi = qualityDpi;
		} else if (
			Number.isFinite(minimumDpi)
			&& minimumDpi > 0
		) {
			resolvedConfigDpi = minimumDpi;
		} else {
			resolvedConfigDpi = 300;
		}
	}

	if (typeof offering.spine === 'string') {
		try {
			const parsedSpine: unknown = JSON.parse(offering.spine);

			if (Array.isArray(parsedSpine)) {
				normalizedSpine = parsedSpine as DB.SpineModel[];
			} else {
				reportError(
					new Error(`Invalid spine data for offering ${offering.id}: ${offering.spine}`),
					'error',
				);
			}
		} catch {
			reportError(
				new Error(`Failed to parse spine for offering ${offering.id}: ${offering.spine}`),
				'error',
			);
		}
	} else if (Array.isArray(offering.spine)) {
		normalizedSpine = offering.spine;
	}

	return {
		...offering,
		configdpi: resolvedConfigDpi,
		externalid: (
			offering.externalid
				? offering.externalid.toString()
				: ''
		),
		spine: normalizedSpine,
	};
}
